import { useQueryClient } from "@tanstack/react-query";
import {
  getQueryKey,
  useGqlMutation,
  useInfiniteGqlQuery,
  useOrgId,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  threadMessagesQuery,
  threadMessagesVariables,
} from "@/app/Threads/GraphQL/threadMessageQueries";
import { sendMessageMutation } from "@/app/Threads/GraphQL/threadMessageMutations";
import { threadRecordQuery } from "@/app/Threads/GraphQL/threadQueries";
import { invalidateThreadQueries } from "@/app/Threads/utils/invalidateThreadQueries";
import { insertOptimisticMessage } from "@/app/Threads/utils/insertOptimisticMessage";
import { useThreadSelection } from "@/app/Threads/hooks/useThreadSelection";
import { useUser } from "@/lib/Auth/useUser";
import { useActiveOrganisation } from "@/app/Organisations/Hooks/useActiveOrganisation";

export function useThreadMessages(threadId: string) {
  const queryClient = useQueryClient();
  const { holdInList } = useThreadSelection();
  const me = useUser();
  const { activeOrganisation } = useActiveOrganisation();
  const orgId = useOrgId();

  const variables = threadMessagesVariables(threadId);

  const messagesQueryKey = getQueryKey(
    threadMessagesQuery,
    variables,
    orgId,
    true,
  );

  const threadQueryKey = getQueryKey(
    threadRecordQuery,
    { id: threadId },
    orgId,
  );

  const result = useInfiniteGqlQuery(threadMessagesQuery, variables, {
    initialPageParam: { page: 1 },
    getNextPageParam: (lastPage) => {
      return lastPage.threadMessages?.paginatorInfo.hasMorePages
        ? {
            page: lastPage.threadMessages.paginatorInfo.currentPage + 1,
          }
        : undefined;
    },
  });

  const messages = result.data?.pages.flatMap(
    (page) => page.threadMessages?.data ?? [],
  );

  const sendMessage = useGqlMutation(sendMessageMutation, {
    onMutate: async (variables: any) => {
      await queryClient.cancelQueries({ queryKey: messagesQueryKey });

      // Sending reopens a closed thread server-side, so the refetch behind this
      // drops it from the closed list. That is a side effect of replying rather
      // than something aimed at the list, so hold the row — while it is still
      // listed, which is now, not when the refetch lands.
      const thread = queryClient.getQueryData<{
        thread?: { closed: boolean } | null;
      }>(threadQueryKey)?.thread;

      if (thread?.closed) {
        holdInList(threadId);
      }

      return insertOptimisticMessage(queryClient, messagesQueryKey, {
        threadId,
        body: variables.body,
        author: me,
        organisation: activeOrganisation ?? null,
        isAdminNote: false,
      });
    },
    onError: (_err: any, _vars: any, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(messagesQueryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["ThreadMessages.Infinite"],
      });
      // Sending reopens a closed thread, so the lists move too. Deliberately
      // not returned: the composer is done once the message is sent, and
      // shouldn't sit in its sending state while the lists catch up.
      invalidateThreadQueries(queryClient);
    },
  });

  return {
    messages,
    isLoading: result.isLoading,
    hasNextPage: result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    sendMessage,
  };
}
